import React, { FC, useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import ResultDetails from "./ResultDetails";
import Gap from "../Gap";
import Icon from "../Icon";
import RText from "../RText";
import { FlagIcon } from "../Icon/Icon";
import AnimatedDropdownWrapper from "../Animations/DropdownAnimation";
import { useResponsive } from "../../hooks/useResponsive";
import variables from "../../styles/variables";
import {
  Meeting,
  Meetings,
  ResultedRace as TResultedRace,
} from "../../types/resultedRaces";
import { formatDateLong, formatRaceTime } from "../../utils/TimeCalculator";
import { getRaceType } from "../../utils/race";
import StatusMessage from "../Screens/Races/Racecards/components/StatusMessage";
import { Racecourse } from "../../types/racecourse";
import RHeading from "../RHeading/RHeading";
import { removeBracketsContainingPhrase } from "../../utils/removeBracketsContainingPhrase";
import { Track } from "../../types/races/racecard";

type ResultedRacesProps = {
  meetings: Meetings;
  headerOverride?: string;
  trackOverride?: Racecourse | Track;
  openOnMount?: boolean;
  openAtIndex?: number | undefined;
};

const ResultedRaces: FC<ResultedRacesProps> = ({
  meetings,
  headerOverride = undefined, // overides used on racecourse page, its ugly but it works
  trackOverride = undefined,
  openOnMount = false,
  openAtIndex = undefined,
}) => {
  return (
    <View style={{ width: "100%" }}>
      {meetings.map((meeting, index) => {
        const open = openOnMount || index === openAtIndex;

        return (
          <ResultedRace
            key={`race-${index}`}
            meeting={meeting}
            trackOverride={trackOverride}
            headerOverride={headerOverride}
            openOnMount={open}
          />
        );
      })}
    </View>
  );
};

type ResultedRaceProps = {
  meeting: Meeting;
  trackOverride?: any;
  headerOverride?: string;
  openOnMount?: boolean;
};

const ResultedRace: FC<ResultedRaceProps> = ({
  meeting,
  trackOverride,
  headerOverride,
  openOnMount = false,
}) => {
  const [open, setOpen] = useState(openOnMount);

  return (
    <View>
      <Header
        open={open}
        setOpen={setOpen}
        meeting={meeting}
        headerOverride={headerOverride}
      />
      <AnimatedDropdownWrapper open={open}>
        {meeting.completed_races?.map((race, index) => (
          <ResultDetails
            key={`resulted-race-${index + 1}`}
            race={race}
            meeting={meeting}
            track={!!trackOverride ? trackOverride : meeting.track}
            showTrackName={false}
          />
        ))}
      </AnimatedDropdownWrapper>
    </View>
  );
};

type HeaderProps = {
  open: boolean;
  setOpen: Function;
  meeting: Meeting;
  headerOverride: string;
};

const Header: FC<HeaderProps> = ({
  open,
  setOpen,
  meeting,
  headerOverride,
}) => {
  const { isLargeAndUp } = useResponsive();

  // Hack used to display races on the racecourse page
  if (!!headerOverride) {
    return (
      <View style={styles.HeaderContainer}>
        <View
          style={{
            justifyContent: "center",
            alignItems: "center",
            paddingLeft: 12,
          }}
        >
          <RHeading level="3" uppercase boldText>
            {headerOverride}
          </RHeading>
        </View>
        <ChevronButton open={open} setOpen={setOpen} />
      </View>
    );
  }

  const flag = meeting?.track?.country_code?.iso;

  const statusMessage = meeting?.status?.state;
  const showStatusMessage =
    !statusMessage ||
    statusMessage == "resulted" ||
    statusMessage == "scheduled";

  return (
    <View style={styles.container}>
      <TouchableOpacity
        style={styles.HeaderContainer}
        onPress={() => setOpen(!open)}
      >
        <View style={styles.alignCenter}>
          <>
            {flag && (
              <Icon
                name={`flag_${flag}` as FlagIcon}
                size="large"
                style={[styles.icon, { marginLeft: 10 }]}
              />
            )}
            {meeting.weather_icon ? (
              <Icon
                name={meeting.weather_icon}
                style={[styles.icon, { marginLeft: 10 }]}
                size="xxlarge"
              />
            ) : (
              <>
                <Gap size="medium" />
                <Gap size="small" />
                <Gap size="xxsmall" />
              </>
            )}
          </>

          <RText
            family="secondary"
            weight="bold"
            size="md_lg"
            style={styles.header}
            uppercase
          >
            {meeting?.track?.name || formatDateLong(meeting.date)}
          </RText>

          {meeting?.track?.tv_channel === "RTV" ? (
            <Icon
              name="rLogo"
              size="medium"
              fill="palette.neutral.x0"
              style={[
                styles.icon,
                { minWidth: 20, maxWidth: 20, marginLeft: 10 },
              ]}
            />
          ) : null}
        </View>

        <View style={styles.alignCenter}>
          {isLargeAndUp && (
            <MeetingInfo
              races={meeting.completed_races}
              trackCondition={removeBracketsContainingPhrase(
                meeting.track_condition?.capitalize(),
                "in places"
              )}
            />
          )}
          <ChevronButton open={open} setOpen={setOpen} />
        </View>
      </TouchableOpacity>
      {open && !isLargeAndUp && (
        <>
          {showStatusMessage ? (
            <StatusMessage message={meeting.status.state} />
          ) : null}

          <View
            style={{
              justifyContent: "center",
              alignItems: "center",
              borderBottomWidth: 1,
              borderBottomColor: variables.colors.palette.neutral.x100,
              paddingVertical: 10,
            }}
          >
            <MeetingInfo
              races={meeting.completed_races}
              trackCondition={meeting.track_condition?.capitalize()}
            />
          </View>
        </>
      )}
    </View>
  );
};

const ChevronButton = ({ open, setOpen }) => (
  <TouchableOpacity style={styles.alignCenter} onPress={() => setOpen(!open)}>
    <Icon
      name="chevronBoldUp"
      size="small"
      fill="palette.neutral.x0"
      style={[styles.icon, { marginRight: 10 }]}
      rotate={open ? 0 : 180}
    />
  </TouchableOpacity>
);

const getRaceTitle = (
  raceType: string,
  raceCount: number,
  startTime?: string,
  endTime?: string,
  trackCondition?: string
) => {
  const raceTitle = [];

  if (raceType) {
    raceTitle.push(raceType);
  }

  if (raceCount) {
    raceTitle.push(raceCount + " races");
  }

  if (startTime && endTime) {
    raceTitle.push(`${formatRaceTime(startTime)} - ${formatRaceTime(endTime)}`);
  }

  if (trackCondition) {
    raceTitle.push(trackCondition);
  }

  return raceTitle ? raceTitle.join(", ") : "";
};

type MeetingInfoProps = {
  races: TResultedRace[];
  trackCondition: string;
};

const MeetingInfo: FC<MeetingInfoProps> = ({ races, trackCondition }) => {
  const firstRaceStartTime = races[0]?.start_time_scheduled;
  const lastRaceStartTime = races[races?.length - 1]?.start_time_scheduled;
  const raceTypes = races.map((race) => race.race_type);
  const raceType = getRaceType(raceTypes);
  const raceTitle = getRaceTitle(
    raceType,
    races.length,
    firstRaceStartTime,
    lastRaceStartTime,
    trackCondition
  );
  return (
    <>
      {!!races.length && <RText style={{ marginRight: 20 }}>{raceTitle}</RText>}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 5,
  },
  header: {
    paddingLeft: 10,
  },
  HeaderContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    height: 40,
    width: "100%",
    backgroundColor: variables.colors.palette.neutral.bar,
  },
  alignCenter: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
  },
  icon: {
    alignSelf: "center",
  },
});

export default ResultedRaces;
